import React, { useCallback, useEffect, useMemo, useRef, useState, Component } from "react";

// COMPONENTS
import Cards from "../../components/Cards";
import CustomChart from "../../components/ChartComponents/BarChart";
import InputField from "../../components/InputField";
import { FormProvider, useForm } from "react-hook-form";
import PromptSuggestions from "../../components/PromptSuggestions";
import Icons from "../../components/Icons";
import { InfoSettings, ControlIcon, BrandGAnalytics, OogwaiLogo3, ChevronOutlineRightIcon, Trash2Icon, PinChatIcon, ExpandIcon, CollapseIcon, EditChatIcon, TrashIcon, DownloadIcon, BrandShopify, BrandMeta, BrandGAds, CustomIntegrationLogo } from "../../components/Icons/iconImages";
import Accordion from "../../components/Accordion";
import { Button } from "../../components/Buttons";

import { Modal as AntdModal, Skeleton, Tooltip, Dropdown } from 'antd';

import FirstTime from "./firsttime";
import { useGetAnalyticStatusQuery, useGetIntegrationErrorsQuery, useGetListAnalyticsAccountsQuery } from "../../services/integrationSlice";
import Loading from "../../components/Loading";
import gsap from "gsap";

import { useReactToPrint } from 'react-to-print';

import {
  useAutoSuggestionMutation,
  useCreateDashboardMutation,
  useDeleteDashboardMutation,
  // useGetDefaultPropertiesConversationQuery - removed, using localStorage
  useLazyAskGoogleAnalyticsQuestionQuery,
  useLazyEditDashboardQuery,
  useLazyGetBookmarksQuery,
  useLazySendFeedBackQuestionQuery,
  useRemoveBookmarkMutation
} from "../../services/dashboardSlice";
import AnimatedSVG from "../../components/Loader";
import { useDebounce } from "../../Hooks/useDebounceHook";
import { useNavigate } from "react-router-dom";

import { TableData, DynamicTable } from "../../components/Table";
import { useGetProfilesQuery } from "../../services/createProfile";
import IconRings from "../../components/IconRings";
import { toast } from "../../components/Toast/toast";
import { useSelector } from "react-redux";
import { useWorkspace } from "../../contexts/WorkspaceContext";

// Helper function to format metric values
const formatMetricValue = (value, key) => {
  if (value === null || value === undefined) return '-';

  // Check if it's a number
  if (typeof value === 'number') {
    // Format as percentage if key contains percentage-related terms
    const percentageKeywords = ['rate', 'percentage', '%', 'ratio', 'percent'];
    const isPercentage = percentageKeywords.some(keyword =>
      key.toLowerCase().includes(keyword)
    );

    // Format as currency if key contains currency-related terms
    const currencyKeywords = ['spend', 'revenue', 'cost', 'price', 'amount', 'inr', 'usd', 'sales'];
    const isCurrency = currencyKeywords.some(keyword =>
      key.toLowerCase().includes(keyword)
    );

    if (isPercentage) {
      return `${value.toFixed(1)}%`;
    } else if (isCurrency) {
      return value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    } else if (Number.isInteger(value)) {
      return value.toLocaleString();
    } else {
      return value.toFixed(2);
    }
  }

  return String(value);
};

// MetricCard component for displaying latest date metrics (stacked vertically)
const MetricCard = ({ label, value }) => (
  <div className="flex flex-col bg-white border border-[#E9EAEB] rounded-xl px-2 py-5">
    <span className="text-xs mb-2">{label}</span>
    <span className="text-4xl font-bold text-[#DC6803]">{value}</span>
  </div>
);

// LatestDateMetrics component - left side panel with date dropdown and stacked metrics
const LatestDateMetrics = ({ data }) => {
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);

  // Check if data has a Date field
  const dateKey = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;
    return Object.keys(data[0] || {}).find(key => key.toLowerCase() === 'date');
  }, [data]);

  // Sort data by date descending and get all unique dates
  const sortedData = useMemo(() => {
    if (!dateKey) return [];
    return [...data].sort((a, b) => {
      const dateA = new Date(a[dateKey]);
      const dateB = new Date(b[dateKey]);
      return dateB - dateA;
    });
  }, [data, dateKey]);

  if (!dateKey || sortedData.length === 0) return null;

  const selectedEntry = sortedData[selectedDateIndex] || sortedData[0];

  // Get all metric keys (exclude the date key)
  const metricKeys = Object.keys(selectedEntry).filter(key => key !== dateKey);

  if (metricKeys.length === 0) return null;

  // Helper function to format date for display
  const formatDateLabel = (dateValue) => {
    const date = new Date(dateValue);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const currentDateLabel = formatDateLabel(selectedEntry[dateKey]);

  // Build dropdown menu items for antd Dropdown
  const dropdownMenuItems = sortedData.slice(0, 30).map((item, index) => ({
    key: index.toString(),
    label: formatDateLabel(item[dateKey]),
    onClick: () => setSelectedDateIndex(index),
  }));

  return (
    <div className="flex flex-col bg-[#FAFAFA] border border-[#E9EAEB] rounded-xl p-2 w-[336px] flex-shrink-0 h-fit">
      {/* Date dropdown header */}
      <div className="flex items-center justify-between mb-3">
        <Dropdown
          menu={{
            items: dropdownMenuItems,
            selectedKeys: [selectedDateIndex.toString()],
            style: { maxHeight: '200px', overflowY: 'auto' }
          }}
          trigger={['click']}
          placement="bottomLeft"
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
        >
          <div className="flex items-center gap-1 border border-gray-200 bg-white rounded-full px-3 py-1 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
            <span>{currentDateLabel}</span>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </Dropdown>
        <InfoSettings className="w-4 h-4 cursor-pointer text-[#535862]" />
      </div>

      {/* Stacked metric cards */}
      <div className="flex flex-col gap-2">
        {metricKeys.slice(0, 2).map((key) => (
          <MetricCard
            key={key}
            label={key}
            value={formatMetricValue(selectedEntry[key], key)}
          />
        ))}
      </div>
    </div>
  );
};

// Error Boundary Component for Charts
class ChartErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorKey: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Chart rendering error:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset error state when data changes
    if (prevProps.data !== this.props.data && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center p-8 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-600">Error presenting chart data</p>
            <p className="text-xs text-gray-500 mt-1">The visualization could not be rendered</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const Dashboard = ({ activeDashboard }) => {
  const navigate = useNavigate()
  const methods = useForm();
  const [search, setSearch] = useState("");
  const dashboardSidebarRef = useRef(null);
  const dashboardContentRef = useRef(null);
  const _onChangeDebounced = async (text) => {
    if (text.length > 2) {
      setSearch(text);
    }
  };

  const { getCurrentWorkspaceObjectId, isInitialized } = useWorkspace();
  const [workspace_id, setWorkSpaceId] = useState(getCurrentWorkspaceObjectId());
  const [isGraphResponsive, setGraphResponsive] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data: profilesData, refetch } = useGetProfilesQuery({
    refetchOnMountOrArgChange: false,
  });

  useEffect(() => {
    // Update workspace_id from context
    setWorkSpaceId(getCurrentWorkspaceObjectId());
  }, [getCurrentWorkspaceObjectId])


  // Helper function to get the appropriate icon component
  const getIntegrationIcon = useCallback((integrationType) => {
    const iconMap = {
      googleanalytics: BrandGAnalytics,
      shopify: BrandShopify,
      meta: BrandMeta,
      googleads: BrandGAds,
      custom_bigquery: CustomIntegrationLogo,
    };

    return iconMap[integrationType] || BrandGAnalytics;
  }, []);

  const tabs = [
    { label: 'Dashboard', icon: <PinChatIcon className="w-[1rem] h-[1rem]" />, value: '' },
    { label: 'Ask Oogwai', icon: <OogwaiLogo3 className="w-[1.25rem] h-[1.25rem]" />, value: 'conversation' },
  ];
  const tabRefs = useRef([]);
  const [activeTab, setActiveTab] = useState(tabs[0].value);

  const onChangeDebounced = useDebounce(_onChangeDebounced, 500);
  const [sendPromtApi] = useLazySendFeedBackQuestionQuery();
  // get auto suggestions
  const [getSuggestion, { isLoading: suggestionLoading, isSuccess: suggestionSuccess }] = useAutoSuggestionMutation();
  const [getBookmarks, { isLoading: bookmarkLoading, isSuccess: bookmarkSuccess }] = useLazyGetBookmarksQuery();
  const [removeBookmark] = useRemoveBookmarkMutation();
  const [suggestions, setSuggestions] = useState([])

  const { data: analyticStatus, isLoading, refetch: refetchAnalyticsStatus } = useGetAnalyticStatusQuery();
  const { register, control, getValues, setValue } = methods;

  const suggestionRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (suggestionLoading) {
      gsap.fromTo(
        suggestionRef.current,
        { height: 0, opacity: 0 },
        { height: "auto", opacity: 1, duration: 0.5, ease: "power2.out" }
      );
    } else {
      gsap.to(suggestionRef.current, { height: 0, opacity: 0, duration: 0.3, ease: "power2.in" });
    }
  }, [suggestionLoading]);

  useEffect(() => {
    if (suggestions?.length > 0 && suggestionsRef.current) {
      gsap.fromTo(
        suggestionsRef.current,
        { height: 0, opacity: 0 },
        { height: "auto", opacity: 1, duration: 0.5, ease: "power2.out" }
      );
    } else if (suggestionsRef.current) {
      gsap.to(suggestionsRef.current, { height: 0, opacity: 0, duration: 0.3, ease: "power2.in" });
    }
  }, [suggestions]);

  // USER QUESTION INPUT
  const { data: listAnalyticsData } = useGetListAnalyticsAccountsQuery({});
  // Get selected accounts from localStorage (same as conversation page)
  const getSelectedAccountsFromStorage = () => {
    const workspaceId = getCurrentWorkspaceObjectId();
    const storageKey = `selected-accounts-${workspaceId}`;
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to parse stored account selection:', error);
      return null;
    }
  };

  const defaultPropertiesData = getSelectedAccountsFromStorage();
  const defaultPropertiesLoading = false;

  const [askGoogleAnayticsQuestion, { isFetching: isQuestionLoading, data: questionData }] = useLazyAskGoogleAnalyticsQuestionQuery({
    refetchOnMountOrArgChange: true,
  });

  const [dashboardListData, setDashboardListData] = useState()
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false)

  useEffect(() => {
    if (!profilesData) return; // Wait until profilesData is defined
    if (!isInitialized) return; // Wait until workspace is initialized
    if (isLoadingBookmarks) return; // Prevent multiple simultaneous calls

    // Scroll to top when dashboard changes
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Clear widgets first to ensure clean state
    setWidgets([]);

    if (activeDashboard) {
      handleSavedBookmarks(activeDashboard);
    } else {
      handleSavedBookmarks();
    }
  }, [profilesData, activeDashboard, isInitialized]); // Added isInitialized dependency

  const workspaceId = useSelector((state) => state.static.workSpaceId);

  useEffect(() => {
    if (workspaceId) {
      refetchAnalyticsStatus();
    }
  }, [workspaceId])

  // Effect for fetching suggestions
  useEffect(() => {
    if (defaultPropertiesData?.property_ids?.length === 0) {
      setSuggestions([]);
      return;
    }
    if (search) {
      let payload = {
        "user_input": search,
        workspace_id
      }
      getSuggestion(payload)
        .unwrap()
        .then((res) => {
          if (res?.suggestions && search.length > 0) {
            setSuggestions((prev) => {
              const existing = new Set(prev.map((item) => item.suggestion)); // or item.name based on uniqueness
              const newSuggestions = res.suggestions.filter(
                (item) => !existing.has(item.suggestion) // or item.name
              );
              return [...prev, ...newSuggestions];
            });
          }
        })
        .catch((error) => {
        })
    }
  }, [search])

  const analyticStatusData = {
    ...analyticStatus,
    message: false,
  }

  const handleTicker = (data, chartType = null) => {
    if (!data || !data.length) return { xLabel: null, yLabel: null, xAxis: null, yAxis: null };
    const lastItem = data[data.length - 1];
    if (!lastItem) return { xLabel: null, yLabel: null, xAxis: null, yAxis: null };
    const keys = Object.keys(lastItem);

    // Analyze field types
    const numericKeys = keys.filter(key =>
      typeof lastItem[key] === 'number' || !isNaN(parseFloat(lastItem[key]))
    );

    // For scatter plots - require numeric fields for both axes
    if (chartType === 'scatter-plot') {
      if (numericKeys.length >= 2) {
        const xAxis = numericKeys[0];
        const yAxis = numericKeys[1];
        return {
          xLabel: xAxis,
          yLabel: yAxis,
          xAxis: xAxis,
          yAxis: yAxis
        };
      } else {
        // Fallback if not enough numeric fields
        return {
          xLabel: numericKeys[0] || keys[0],
          yLabel: numericKeys[1] || keys[1],
          xAxis: numericKeys[0] || keys[0],
          yAxis: numericKeys[1] || keys[1]
        };
      }
    }

    // For other chart types - use original logic
    const xLabel = keys.length > 0 ? keys[0] : null;
    const yLabel = keys.length > 2
      ? keys.filter((item, index) => index > 0 && keys.length > 2 && item !== 'date')
      : keys[1];
    const xAxis = keys.length > 0 ? keys.find(key => key === 'date') ? 'date' : keys[0] : null;
    const yAxis = keys.length > 2
      ? keys.filter((item, index) => index > 0 && keys.length > 2 && item !== 'date')
      : keys[1];

    return {
      xLabel,
      yLabel,
      xAxis,
      yAxis
    };
  }

  // SIDEBAR STATE MANAGEMENT
  const [toggleSidebar, setToggleSidebar] = useState(true);
  const [activeWidget, setActiveWidget] = useState(null); // Tracks the currently active widget
  // const [isAddingWidget, setIsAddingWidget] = useState(false); // Flag for adding widget

  // DYNAMIC WIDGETS
  const colorScheme = ['#1F7A50', '#DC6803', '#E15759', '#FFC107', '#457B9D'];
  const [widgets, setWidgets] = useState([]);
  const [expandedWidgets, setExpandedWidgets] = useState({});

  const [isOpenVisualModal, setOpenVisualModal] = useState({
    state: false,
    type: null,
    response: {}
  });
  const modalKeyRef = useRef(0);

  const [isOpenCreateDashboard, setOpenCreateDashboard] = useState(false);

  // State declarations for the edit modal
  const [isOpenEditDashboard, setOpenEditDashboard] = useState(false);
  const [editingDashboardName, setEditingDashboardName] = useState('');
  const [originalDashboardName, setOriginalDashboardName] = useState('');

  // State for Delete Modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState('');

  // Function to open the Delete Modal
  const openDeleteDashboardModal = (name) => {
    setDashboardToDelete(name);
    setIsDeleteModalOpen(true);
  };

  const widgetsContainerRef = useRef(null);

  const handleRemoveWidget = (id, created_at) => {
    if (!created_at) {
      toast.error('Invalid widget ID');
      return;
    }

    const widgetElement = document.getElementById(id ? `widget-${id}` : `widget-${created_at}`);
    const payload = {
      workspace_id,
      created_at,
    };

    removeBookmark(payload).unwrap()
      .then((res) => {
        toast.success(res.message);

        if (widgetElement) {
          const currentHeight = widgetElement.offsetHeight;
          gsap.to(widgetElement, {
            opacity: 0,
            height: 0,
            marginBottom: 0,
            paddingTop: 0,
            paddingBottom: 0,
            duration: 0.4,
            ease: 'power2.inOut',
            onComplete: () => {
              setWidgets((prevWidgets) => prevWidgets.filter((widget) => widget.created_at !== created_at));
            },
          });
        } else {
          // If element not found, remove from state immediately
          setWidgets((prevWidgets) => prevWidgets.filter((widget) => widget.created_at !== created_at));
        }
      })
      .catch((error) => {
        if (error.status === 400) {
          toast.error('Bad request');
        } else {
          toast.error(error.message);
        }
      });
  };


  const [createDashboard] = useCreateDashboardMutation({});
  const [editDashboard] = useLazyEditDashboardQuery({});
  const [deleteDashboard] = useDeleteDashboardMutation({});

  useEffect(() => {
    if (dashboardListData && !localStorage.getItem('activeDashboard')) {
      localStorage.setItem('activeDashboard', dashboardListData[0].name);
    } else if (localStorage.getItem('activeDashboard') && dashboardListData && dashboardListData.every(dashboard => !dashboard.isActive)) {
      const activeDashboard = dashboardListData.find(dashboard => dashboard.name === localStorage.getItem('activeDashboard'));
      if (activeDashboard) {
        setDashboardListData((prevList) =>
          prevList.map((dashboard) => ({
            ...dashboard,
            isActive: dashboard.name === activeDashboard.name,
          }))
        );
      }
    }
  }, [dashboardListData])

  // Dashboard switching is handled by DashboardLayout component via activeDashboard prop

  const openEditDashboardModal = (name) => {
    setEditingDashboardName(name);      // For the input field (new value)
    setOriginalDashboardName(name);       // To remember the old name for the API call
    setOpenEditDashboard(true);
  };

  const getMenuProps = (dashboardName) => {
    const items = [
      {
        label: (
          <Button
            icon={<EditChatIcon className="w-3 h-3" />}
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openEditDashboardModal(dashboardName);
            }}
            className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
          >
            <span>Edit</span>
          </Button>
        ),
        key: '0',
      },
    ];

    // Only show the Delete option if there is more than one dashboard.
    if (dashboardListData && dashboardListData.length > 1) {
      items.push({
        label: (
          <Button
            icon={<TrashIcon className="w-3 h-3" />}
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDashboardModal(dashboardName);
            }}
            className="ant-dropdown-menu-button text-red-900 flex items-center justify-start space-x-2 w-full"
          >
            <span>Delete</span>
          </Button>
        ),
        key: '1',
      });
    }

    return { items };
  };

  const handleSubmitEditDashboard = async () => {
    try {
      // Prepare payload with both old and new names
      if (originalDashboardName === editingDashboardName) {
        toast.info('Enter a different name');
        return
      }
      const payload = {
        old_name: originalDashboardName,
        new_name: editingDashboardName,
      };

      // Call the editDashboard query (which uses GET) with the payload
      const response = await editDashboard(payload).unwrap();

      if (response.message) {
        toast.success('Dashboard updated successfully', {
          toastId: 'dashboard-updated',
        });
      }
      // Update localStorage if needed
      localStorage.setItem('activeDashboard', editingDashboardName);

      // Refresh dashboard data with the new dashboard name
      await handleSavedBookmarks(editingDashboardName);

      // Close the modal on success
      setOpenEditDashboard(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.data?.error || 'Failed to update dashboard', {
        toastId: 'dashboard-update-failed',
      });
    }
  };

  // Function to handle deletion of a dashboard
  const handleDeleteDashboard = async () => {
    try {
      // Construct your payload. Adjust this if your API requires additional parameters.
      const payload = {
        dashboard_name: dashboardToDelete,
        workspace_id: getCurrentWorkspaceObjectId(),
      };

      const response = await deleteDashboard(payload).unwrap();
      if (response.message) {
        toast.success('Dashboard deleted successfully', { toastId: 'dashboard-deleted' });
      }
      // Optionally, update localStorage if the deleted dashboard was active
      if (localStorage.getItem("activeDashboard") === dashboardToDelete) {
        // Set active dashboard to another dashboard if available.
        localStorage.setItem("activeDashboard", "");
      }
      await handleSavedBookmarks(); // Refresh dashboard list after deletion
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Delete Error:', error);
      toast.error(error.data?.error || 'Failed to delete dashboard', { toastId: 'dashboard-delete-failed' });
    }
  };

  // Helper function to format updated_at timestamp
  const formatUpdateTime = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    // Format time as HH:MM AM/PM
    const timeString = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    if (dateOnly.getTime() === today.getTime()) {
      return `Today at ${timeString}`;
    } else if (dateOnly.getTime() === yesterday.getTime()) {
      return `Yesterday at ${timeString}`;
    } else {
      const monthDay = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${monthDay} at ${timeString}`;
    }
  };

  // Helper function to render text with markdown bold syntax (**...**) styled
  const renderTextWithBoldWords = (text, list_of_words = []) => {
    if (!text) return null;

    const textContent = String(text || "");

    // Parse **...** markdown bold syntax
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = boldRegex.exec(textContent)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push({
          text: textContent.slice(lastIndex, match.index),
          isBold: false
        });
      }
      // Add the bold text (without the ** markers)
      parts.push({
        text: match[1],
        isBold: true
      });
      lastIndex = match.index + match[0].length;
    }

    // Add remaining text after last match
    if (lastIndex < textContent.length) {
      parts.push({
        text: textContent.slice(lastIndex),
        isBold: false
      });
    }

    // If no bold markers found, return plain text
    if (parts.length === 0) {
      return textContent;
    }

    return parts.map((part, index) => {
      if (part.isBold) {
        return (
          <span key={index} className="text-text-700 font-semibold">
            {part.text}
          </span>
        );
      }
      return <span key={index}>{part.text}</span>;
    });
  };

  const handleSavedBookmarks = async (activeDashboard) => {
    try {
      // Prevent multiple simultaneous calls
      if (isLoadingBookmarks) return;
      setIsLoadingBookmarks(true);

      // Determine a valid workspace ID from localStorage or profilesData.
      const storedWorkspaceId = getCurrentWorkspaceObjectId() || profilesData?.workspaces?.[0]?._id;
      if (!storedWorkspaceId) {
        console.error("Workspace ID is not available.");
        setIsLoadingBookmarks(false);
        return;
      }

      // Pass the valid workspace ID to getBookmarks.
      const response = await getBookmarks({ workspace_id: storedWorkspaceId }).unwrap();

      if (!response.dashboards) {
        setIsLoadingBookmarks(false);
        return;
      }

      // Don't auto-set active dashboard - let user select explicitly

      const dashboardList = response.dashboards.map(dashboard => ({
        name: dashboard.dashboard_name,
        isActive: dashboard?.isActive || false
      }));
      setDashboardListData(dashboardList);

      // Load widgets if there's a selected dashboard (including auto-selected active dashboard)
      const targetDashboard = activeDashboard || localStorage.getItem('activeDashboard');

      if (!targetDashboard) {
        // No dashboard selected - don't load any widgets
        setWidgets([]);
        setIsLoadingBookmarks(false);
        return;
      }

      // Find the specific dashboard
      const targetDashboardData = response.dashboards.find(dashboard => dashboard.dashboard_name === targetDashboard);

      if (!targetDashboardData) {
        console.warn(`Dashboard "${targetDashboard}" not found`);
        setWidgets([]);
        setIsLoadingBookmarks(false);
        return;
      }

      const newWidgets = targetDashboardData.bookmarks.map((item, index) => {
        // Get the chat array which may contain multiple agents
        const chatArray = item?.saved_chat?.chat || [];

        // Map visual_tag values to chart types
        const mapChartType = (tag) => {
          const typeMap = {
            'pie': 'pie-chart',
            'pie_chart': 'pie-chart',
            'piechart': 'pie-chart',
            'donut': 'donut-chart',
            'donut_chart': 'donut-chart',
            'bar': 'bar-chart',
            'bar_chart': 'bar-chart',
            'barchart': 'bar-chart',
            'horizontal_bar_chart': 'horizontal-bar',
            'horizontal-bar-chart': 'horizontal-bar',
            'horizontalbarchart': 'horizontal-bar',
            'multi_bar': 'multi-bar',
            'multi_bar_chart': 'multi-bar',
            'multibarchart': 'multi-bar',
            'multi_horizontal_bar_chart': 'multi-horizontal-bar',
            'multi-horizontal-bar-chart': 'multi-horizontal-bar',
            'multihorizontalbarchart': 'multi-horizontal-bar',
            'line': 'line-graph',
            'line_chart': 'line-graph',
            'line_graph': 'line-graph',
            'linegraph': 'line-graph',
            'scatter': 'scatter-plot',
            'scatter_plot': 'scatter-plot',
            'scatterplot': 'scatter-plot',
            'scatter-plot': 'scatter-plot',
            'geo': 'geo-map',
            'geo_map': 'geo-map',
            'geomap': 'geo-map',
            'geo-map': 'geo-map',
            'map': 'geo-map',
            'table': 'table',
            // Dual-axis chart types
            'multi_bar_chart_secondary_y_axis': 'multi-bar-chart-secondary-y-axis',
            'multi-bar-chart-secondary-y-axis': 'multi-bar-chart-secondary-y-axis',
            'line_graph_secondary_y_axis': 'line-graph-secondary-y-axis',
            'line-graph-secondary-y-axis': 'line-graph-secondary-y-axis',
            'bar_graph_secondary_y_axis': 'bar-graph-secondary-y-axis',
            'bar-graph-secondary-y-axis': 'bar-graph-secondary-y-axis'
          };
          return typeMap[tag?.toLowerCase()] || tag || 'bar-chart';
        };

        // Collect all agents data from the chat array
        const agentsData = [];
        const agents = ['googleanalytics', 'shopify', 'meta', 'googleads', 'custom_bigquery'];

        chatArray.forEach(chatItem => {
          if (chatItem?.agent && agents.includes(chatItem.agent)) {
            // Get chart type and data for this agent
            const agentData = chatItem.data ?? {};

            // Infer chart type from visual_tag or from data structure
            let chartType = mapChartType(chatItem?.visual_tag);

            // If visual_tag is missing, infer from data structure
            if (!chatItem?.visual_tag && agentData) {
              switch (true) {
                case Boolean(agentData.pie_chart || agentData.pie):
                  chartType = 'pie-chart';
                  break;
                case Boolean(agentData.donut_chart || agentData.donut):
                  chartType = 'donut-chart';
                  break;
                case Boolean(agentData.line_graph || agentData.line_chart):
                  chartType = 'line-graph';
                  break;
                case Boolean(agentData.scatter_plot || agentData.scatter):
                  chartType = 'scatter-plot';
                  break;
                case Boolean(agentData.geo_map || agentData.geo):
                  chartType = 'geo-map';
                  break;
                case Boolean(agentData.multi_bar_chart || agentData.multi_bar):
                  chartType = 'multi-bar';
                  break;
                case Boolean(agentData.bar_chart || agentData.bar):
                  chartType = 'bar-chart';
                  break;
                default:
                  console.warn(`Unknown chart type for agent ${chatItem.agent}`);
              }
            }

            // Get the actual array data for this agent
            let dataForTicker = agentData;
            if (!Array.isArray(agentData)) {
              dataForTicker = agentData[chartType.replace(/-/g, '_')] ||
                [];
            }

            const tickerData = handleTicker(dataForTicker, chartType);

            agentsData.push({
              agent: chatItem.agent,
              text_response: chatItem.text_response,
              data: chatItem.data,
              query: chatItem.query,
              list_of_words: chatItem.list_of_words,
              updated_at: chatItem.updated_at,
              visual_tag: chatItem.visual_tag,
              chart: {
                data: agentData,
                dataXKey: tickerData?.xAxis || null,
                dataYKey: tickerData?.yAxis || null,
                xAxisLabel: tickerData?.xLabel,
                yAxisLabel: tickerData?.yLabel,
                type: chartType,
                updated_at: agentData.updated_at || chatItem.updated_at || item?.updated_at
              }
            });
          }
        });

        return {
          id: item?.chat_id,
          custom_name: item?.custom_name,
          created_at: item?.created_at,
          message_id: item?.saved_chat?.message_id,
          name: item?.saved_chat?.question,
          agents: agentsData, // Store all agents data
          frequency: item?.frequency,
          colorScheme: colorScheme[Math.floor(Math.random() * colorScheme.length)],
        };
      });

      // Replace widgets instead of appending - clear and set new widgets
      setWidgets(newWidgets);
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while fetching bookmark data', error?.message);
    } finally {
      setIsLoadingBookmarks(false);
    }
  };


  const handleQuestion = async () => {
    try {
      const question = getValues('questionInput');
      if (!question) {
        toast.error('Please enter a question to proceed');
        return;
      };
      navigate("/conversation", {
        state: {
          question: question
        }
      })

      sendPromtApi(question)
        .unwrap()
        .then((res) => { })
        .catch((err) => { })

      const propertyNames = defaultPropertiesData?.property_names || [];

      const gaPropertyIds = propertyNames
        .filter((item) => item.integrationType === "GA4")
        .map((item) => item.propertyId);

      const shopifyPropertyIds = propertyNames
        .filter((item) => item.integrationType === "Shopify")
        .map((item) => item.propertyId);

      const metaPropertyIds = propertyNames
        .filter((item) => item.integrationType === "Meta")
        .map((item) => item.propertyId);

      const adsPropertyIds = propertyNames
        .filter((item) => item.integrationType === "GoogleAds")
        .map((item) => item.propertyId);

      const payload = {
        question: question,
        meta_id: metaPropertyIds.length > 0 ? metaPropertyIds : '',
        google_ads_id: adsPropertyIds.length > 0 ? adsPropertyIds : '',
        shopify_id: shopifyPropertyIds.length > 0 ? shopifyPropertyIds : '',
        property_id: gaPropertyIds.length > 0 ? gaPropertyIds : '',
        chat_id: ""
      };

      const response = await askGoogleAnayticsQuestion(payload)
        .unwrap();

      if (!response) {
        // Handle empty response
        console.error('No data returned from Google Analytics');
        toast.error('No data available for your query');
        return;
      }

      if (!response.data) {
        throw new Error('Unexpected error: Empty data in response');
      }

      // const outputData = response.data.map(item => item.output?.[0]);
      // toast.success(`Output Data: ${outputData.join(', ')}`);
      // if (widgets.length === 0) {
      //   await handleDefaultQuestion();
      // } else {
      // await handleAddWidget(response, question);
      // }

    } catch (error) {
      console.error(error);
      toast.error(error?.data?.error || error || 'Unexpected error');
    }
  }

  const handleWidgetPrompt = useCallback((id, message_id) => {
    try {
      if (!id) {
        throw new Error('Invalid chatID');
      }
      navigate(`/conversation?chatid=${id}`)
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Unexpected error');
    }
  }, [navigate])

  const handleVisualModal = (modalType, modalState = false, response) => {
    if (modalState) {
      // Increment key when opening modal to force table reset
      modalKeyRef.current += 1;
    }
    setOpenVisualModal({
      state: modalState,
      type: modalType,
      response: response
    });
  }

  const toggleWidgetExpansion = (widgetKey) => {
    setExpandedWidgets(prev => ({
      ...prev,
      [widgetKey]: !prev[widgetKey]
    }));
  };

  const widgetList = useMemo(() => {
    return widgets.map((widget, index) => {
      const widgetKey = widget.id || widget.created_at;
      const isExpanded = expandedWidgets[widgetKey];

      // =========================
      // Determine last updated timestamp
      // =========================
      let updatedAt = widget?.updated_at;

      // Try pulling latest agent update if available
      if (widget?.agents?.length) {
        const latestAgentUpdate = widget.agents
          .map((a) => a?.updated_at || a?.chart?.updated_at)
          .filter(Boolean)
          .sort((a, b) => new Date(b) - new Date(a))[0];
        if (latestAgentUpdate) {
          updatedAt = latestAgentUpdate;
        }
      }

      // Fallback to widget creation if nothing else
      if (!updatedAt) updatedAt = widget?.created_at;

      return (
        <div
          id={`widget-${widgetKey}`}
          key={widgetKey}
          className="flex flex-col w-full border border-[#E9EAEB] rounded-xl p-3 shadow-sm print:overflow-visible print:max-h-none"
        >
          {/* ───────────────────── Header Section ───────────────────── */}
          <div className="flex flex-col md:flex-row items-start justify-between pb-4 gap-2">
            {/* Title */}
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <h4 className="font-semibold capitalize break-words">
                {widget?.custom_name || widget?.name}
              </h4>
            </div>

            {/* Buttons (Last update, Delete, Prompt) */}
            <div className="flex gap-2 items-center justify-end w-full md:w-auto flex-wrap">
              {updatedAt && (
                <p className="font-medium text-[0.65rem] min-[400px]:text-xs border border-[#E9EAEB] rounded-full px-2 py-1 bg-gray-100 text-gray-700 max-w-full break-words">
                  <span className="hidden min-[400px]:inline">Last Update: </span>
                  <span className="whitespace-nowrap">{formatUpdateTime(updatedAt)}</span>
                </p>
              )}

              <Button
                danger
                icon={<Trash2Icon />}
                type="text"
                shape="circle"
                onClick={() => handleRemoveWidget(widget.id, widget.created_at)}
              />

              <Button
                type="default"
                onClick={() => handleWidgetPrompt(widget.id)}
              >
                View Conversation
              </Button>
            </div>
          </div>

          {/* ───────────────────── Agent Data Section ───────────────────── */}
          {widget.agents?.length ? (
            widget.agents.map((agentData, idx) => {
              const agentKey = `${widgetKey}-agent-${idx}`;
              const isAgentExpanded = expandedWidgets[agentKey];
              const textLength = agentData.text_response?.length || 0;

              // Check for valid chart/table data
              const chartData = agentData?.chart?.data || {};
              const hasTableData =
                (Array.isArray(chartData?.table) && chartData.table.length > 0) ||
                (Array.isArray(chartData) && chartData.length > 0) ||
                Object.values(chartData).some(
                  (v) => Array.isArray(v) && v.length > 0
                );

              // ───── Agent Section ─────
              return (
                <div key={idx} className="mb-4 border-b border-gray-300 last:border-b-0 pb-4 last:pb-0">
                  {/* Agent Text */}
                  <div className="flex items-start gap-2 mb-3">
                    <Tooltip
                      title={agentData.agent.charAt(0).toUpperCase() + agentData.agent.slice(1)}
                      placement="top"
                    >
                      {React.createElement(getIntegrationIcon(agentData.agent), {
                        className: 'w-10 h-10 flex-shrink-0 mt-0.5',
                      })}
                    </Tooltip>

                    <div className="flex-1">
                      <p
                        className={`font-normal text-sm text-[#535862] whitespace-pre-wrap break-words p-1 ${!isAgentExpanded ? 'line-clamp-2' : ''
                          }`}
                      >
                        {renderTextWithBoldWords(agentData.text_response, agentData.list_of_words)}
                      </p>

                      {textLength > 150 && (
                        <button
                          onClick={() => toggleWidgetExpansion(agentKey)}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-1 focus:outline-none"
                        >
                          {isAgentExpanded ? 'View less' : 'View more'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ───── Table + Charts ───── */}
                  {hasTableData && (() => {
                    // Detect and prepare charts
                    const availableCharts = [];
                    const chartTypeMap = {
                      'pie': 'pie-chart',
                      'pie_chart': 'pie-chart',
                      'piechart': 'pie-chart',
                      'donut': 'donut-chart',
                      'donut_chart': 'donut-chart',
                      'bar': 'bar-chart',
                      'bar_chart': 'bar-chart',
                      'barchart': 'bar-chart',
                      'multi_bar': 'multi-bar',
                      'multi_bar_chart': 'multi-bar',
                      'multibarchart': 'multi-bar',
                      'multi_bar_chart_secondary_y_axis': 'multi-bar-chart-secondary-y-axis',
                      'multi-bar-chart-secondary-y-axis': 'multi-bar-chart-secondary-y-axis',
                      'line_graph_secondary_y_axis': 'line-graph-secondary-y-axis',
                      'line-graph-secondary-y-axis': 'line-graph-secondary-y-axis',
                      'bar_graph_secondary_y_axis': 'bar-graph-secondary-y-axis',
                      'bar-graph-secondary-y-axis': 'bar-graph-secondary-y-axis',
                      'horizontal_bar_chart': 'horizontal-bar',
                      'horizontal-bar-chart': 'horizontal-bar',
                      'horizontalbarchart': 'horizontal-bar',
                      'multi_horizontal_bar_chart': 'multi-horizontal-bar',
                      'multi-horizontal-bar-chart': 'multi-horizontal-bar',
                      'multihorizontalbarchart': 'multi-horizontal-bar',
                      'line': 'line-graph',
                      'line_graph': 'line-graph',
                      'area': 'area-chart',
                      'area_chart': 'area-chart',
                      'areachart': 'area-chart',
                      'scatter': 'scatter-plot',
                      'scatter_plot': 'scatter-plot',
                      'table': 'table'
                    };

                    // If chartData is array (legacy)
                    if (Array.isArray(chartData)) {
                      availableCharts.push({
                        type: agentData.chart?.type || 'bar-chart',
                        data: chartData,
                      });
                    } else {
                      // New structured format
                      Object.entries(chartData).forEach(([key, data]) => {
                        if (key === 'table') return;
                        const chartType = chartTypeMap[key];
                        if (!chartType) return;

                        // Handle even empty arrays (we’ll show “No Data” in chart)
                        const tickerData = handleTicker(data || [], chartType);
                        availableCharts.push({
                          type: chartType,
                          data: data || [],
                          dataXKey: tickerData?.xAxis,
                          dataYKey: tickerData?.yAxis,
                          xAxisLabel: tickerData?.xLabel,
                          yAxisLabel: tickerData?.yLabel,
                        });
                      });
                    }

                    // Filter only charts that exist (even empty arrays are okay for "No Data")
                    const validCharts = availableCharts.length > 0 ? availableCharts : [];

                    // Get the primary data array for metrics panel
                    const primaryDataForMetrics = chartData?.table ||
                      (Array.isArray(chartData) ? chartData : null) ||
                      (validCharts.length > 0 ? validCharts[0].data : null);

                    // Check if data has date field for showing metrics panel
                    const hasDateField = primaryDataForMetrics &&
                      Array.isArray(primaryDataForMetrics) &&
                      primaryDataForMetrics.length > 0 &&
                      Object.keys(primaryDataForMetrics[0] || {}).some(
                        key => key.toLowerCase() === 'date'
                      );

                    return (
                      <div className="flex flex-wrap w-full gap-4 mt-3">
                        {/* ─────────────── Latest Date Metrics Panel ─────────────── */}
                        {hasDateField && (
                          <LatestDateMetrics data={primaryDataForMetrics} />
                        )}

                        {/* ─────────────── Table Section ─────────────── */}
                        {(chartData?.table?.length > 0 ||
                          (Array.isArray(chartData) && chartData.length > 0)) && (
                            <div className="relative flex-1 w-full overflow-hidden" style={{ minWidth: '260px' }}>
                              <Button
                                onClick={() =>
                                  handleVisualModal('table', true, { ...widget, chart: agentData.chart })
                                }
                                shape="circle"
                                className="bg-gray-100 absolute right-1 top-1 z-10 print-hidden opacity-60 hover:opacity-100 transition-opacity duration-200"
                                icon={<ExpandIcon className="w-4 h-4" />}
                                title="Expand table"
                              />
                              <TableData
                                pageSizeLimit={isGraphResponsive ? 6 : 'no-limit'}
                                data={chartData?.table || chartData}
                                isFetching={isQuestionLoading}
                                className="print:w-h-full print:overflow-hidden min-w-0 w-full"
                              />
                            </div>
                          )}

                        {/* ─────────────── Charts Section ─────────────── */}
                        {validCharts.map((chart, chartIdx) => {

                          // Adaptive height logic based on total chart count
                          let maxWidth = 400;
                          let width = '100%'

                          // Slightly smaller for pie/donut
                          if (["pie-chart", "donut-chart"].includes(chart.type)) {
                            if (Array.isArray(chart?.dataYKey) && chart?.dataYKey?.length > 4) {
                              maxWidth = 800;
                            } else {
                              maxWidth = 500;
                            }
                          }

                          if (chart.type === "bar-chart" || chart.type === "line-graph" || chart.type === "area-chart" || chart.type === "multi-bar-chart-secondary-y-axis" || chart.type === "line-graph-secondary-y-axis" || chart.type === "bar-graph-secondary-y-axis") {
                            const dataLength = chart?.data?.length || 0;

                            // Set a base width and an increment per item
                            const BASE_WIDTH = 300;
                            const WIDTH_PER_ITEM = 50;
                            const MAX_CAP = 1200; // Prevent the chart from growing indefinitely

                            // Calculate the width: Base + (Number of items * Width per item)
                            maxWidth = Math.min(
                              BASE_WIDTH + (dataLength * WIDTH_PER_ITEM),
                              MAX_CAP
                            );
                          }

                          return (
                            <Cards
                              key={`${idx}-${chartIdx}`}
                              classname="p-3 h-fit"
                              childClassName="flex flex-col justify-between gap-2 h-fit w-full"
                              variant="secondary"
                              style={{ maxWidth: `${maxWidth}px`, flex: '1 1 300px', width: width }}
                            >
                              <ChartErrorBoundary data={chart.data}>
                                <CustomChart
                                  data={chart.data}
                                  type={chart.type}
                                  size={isMobile ? 'small' : 'medium'}
                                  dataXkey={chart.dataXKey}
                                  dataYKey={chart.dataYKey}
                                  xAxisLabel={chart.xAxisLabel}
                                  yAxisLabel={chart.yAxisLabel}
                                  fill={widget.colorScheme}
                                  className="w-full"
                                  isResponsive={true}
                                  showExpandButton={true}
                                  onExpand={() =>
                                    setOpenVisualModal({
                                      state: true,
                                      type: 'graph',
                                      response: {
                                        ...widget,
                                        chart: {
                                          ...agentData.chart,
                                          type: chart.type,
                                          data: chart.data,
                                          dataXKey: chart.dataXKey,
                                          dataYKey: chart.dataYKey,
                                          xAxisLabel: chart.xAxisLabel,
                                          yAxisLabel: chart.yAxisLabel,
                                        },
                                      },
                                    })
                                  }
                                />
                              </ChartErrorBoundary>
                            </Cards>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              );
            })
          ) : (
            // ───── No Agent Case ─────
            <div>
              <p
                className={`font-normal text-sm text-[#535862] whitespace-pre-wrap break-words ${!isExpanded ? 'line-clamp-2' : ''
                  }`}
              >
                {renderTextWithBoldWords(widget.prompt, widget.list_of_words)}
              </p>

              {widget.prompt?.length > 150 && (
                <button
                  onClick={() => toggleWidgetExpansion(widgetKey)}
                  className="text-xs text-blue-600 hover:text-blue-800 mt-1 focus:outline-none"
                >
                  {isExpanded ? 'View less' : 'View more'}
                </button>
              )}
            </div>
          )}
        </div>
      );
    });
  }, [widgets, isGraphResponsive, expandedWidgets]);


  const handleCreateDashboard = async () => {
    try {
      setOpenCreateDashboard(true);
      const dashboardName = getValues('dashboard_name');
      if (!dashboardName) {
        toast.error('Please enter a dashboard name', {
          toastId: 'dashboard-name-required'
        });
        return;
      }
      const response = await createDashboard({ dashboard_name: dashboardName }).unwrap();

      if (response.message) {
        toast.success('Dashboard created successfully', {
          toastId: 'dashboard-created'
        });
      }
      setValue('dashboard_name', '');
      setOpenCreateDashboard(false);

      // Set the new dashboard as active.
      localStorage.setItem('activeDashboard', dashboardName);

      // Refresh the dashboard list and pass the new active dashboard.
      await handleSavedBookmarks(dashboardName);

    } catch (error) {
      console.error('Error:', error);
      toast.error(error.data?.error || 'Failed to create dashboard', {
        toastId: 'dashboard-creation-failed'
      });
    }
  };

  const _handlePrint = useReactToPrint({
    contentRef: widgetsContainerRef,
    documentTitle: 'DashboardWidgets',
    removeAfterPrint: true,
    onBeforeGetContent: () => {
      window.dispatchEvent(new Event('resize'));
    },
    onAfterPrint: () => setGraphResponsive(true),
  });

  const handlePrint = useCallback(() => {
    if (isGraphResponsive) {
      setGraphResponsive(false);
      setTimeout(_handlePrint, 200);
    } else {
      setGraphResponsive(true);
    }
  }, []);


  if (bookmarkLoading || isLoading) {
    return <Loading />
  }

  if (widgets.length === 0 && Object.values(analyticStatusData || {}).every((value) => !value)) {
    return <FirstTime />
  }

  return (
    <FormProvider {...methods}>
      <div className="relative">
        <div ref={dashboardContentRef}>
          <section className="flex flex-col w-full gap-6 pt-6 pl-0 md:pl-6">
            <div className="grid grid-cols-1 divide-y gap-2">
              <div className="pb-3 flex items-center justify-between w-full">
                <h4 className="font-semibold">Dashboard</h4>
                <Button
                  type="default"
                  className="h-11 min-w-11"
                  onClick={() => handlePrint()}
                  disabled={widgetList.length === 0}
                  icon={<DownloadIcon className="w-4 h-4" />}
                >
                  PDF print
                </Button>
                {/* <p className="text-text-600 text-sm">This is where all your query widgets live</p> */}
              </div>
              <div className={`${(suggestions.length || suggestionLoading) ? 'space-y-3' : 'space-y-1'} py-2`} data-tour="widgets">
                {/* <div className="bg-gray-100 py-10 px-3 rounded-2xl">
                  <div className="flex justify-between gap-2 max-w-[584px] mx-auto">
                    <OogwaiLogo3 className="min-w-12 h-12" />
                    <InputField
                      name="questionInput"
                      hint={suggestionLoading ? "generating suggestions..." : suggestionSuccess ? "suggestions generated" : "e.g., ask about audience numbers, conversion rate, bounce rate, sales, etc."}
                      placeholder="ask something"
                      control={control}
                      iconDirection={"right"}
                      customLoadingIcon={<AnimatedSVG />}
                      disabled={isQuestionLoading}
                      isLoading={isQuestionLoading}
                      onKeyPress={(event) => {
                        if (defaultPropertiesData?.property_names?.length === 0) {
                          toast.warning("Please select at least one property to ask questions.", { id: 'default-properties-saved' });

                          return;
                        }
                        if (event.key === 'Enter') {
                          handleQuestion();
                        }
                      }}
                      onChange={(e) => {
                        if (defaultPropertiesData?.property_names?.length === 0) {
                          // toast.error('Please select a property ID to proceed');
                          return;
                        }
                        onChangeDebounced(e.target.value);
                      }}
                    />
                    <Button
                      type="primary"
                      onClick={() => {
                        if (defaultPropertiesData?.property_names?.length === 0) {
                          toast.warning("Please select at least one property to ask questions.", { id: 'default-properties-saved' });
                          return;
                        }
                        handleQuestion()
                      }}
                      className="h-11 min-w-11"
                      loading={isQuestionLoading}
                      icon={<RightArrowIcon />}
                    />
                  </div>
                </div> */}

                {/* Loading Suggestions */}
                <div ref={suggestionRef} className="overflow-hidden">
                  {suggestionLoading && (
                    <div className="flex justify-between gap-2 max-w-[584px] mx-auto">
                      <AnimatedSVG />
                    </div>
                  )}
                </div>

                {/* Suggestions */}
                <div >
                  {suggestions?.length > 0 && (
                    <div className="overflow-hidden w-full pb-2">
                      <PromptSuggestions
                        className="w-full"
                        promptData={suggestions}
                        sendPromtApi={(prompt) => {
                          setValue("questionInput", prompt);
                          setSuggestions([]);
                        }}
                      />
                    </div>
                  )}
                </div>
                {/* <div className="">
                  <Button
                    type="text"
                    className="h-11 min-w-11"
                    onClick={() => handlePrint()}
                    disabled={widgetList.length === 0}
                    icon={<DownloadIcon className="w-4 h-4" />}
                  >
                    PDF print
                  </Button>
                </div> */}
                <div
                  className="flex w-full flex-col gap-5 print:break-inside-avoid"
                  ref={widgetsContainerRef}

                >
                  {/* Loading Widgets */}
                  {
                    (isQuestionLoading) && (
                      <div aria-disabled className="relative flex flex-col w-full border border-[#E9EAEB] rounded-xl p-3 opacity-50">
                        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                          Loading data...
                        </div>
                        <div className="flex items-center justify-between pb-4">
                          <div className="flex items-center gap-2">
                            <OogwaiLogo3 className="w-8 h-8" />
                          </div>
                          <div className="flex gap-2 items-center">
                            <Button
                              danger
                              icon={<Trash2Icon />}
                              type="text"
                              shape="circle"
                            // className="h-8 w-8 text-white rounded-full"
                            />
                            <Button
                              type="default"
                            >
                              View Conversation
                            </Button>
                          </div>
                        </div>
                        <div className="flex w-full gap-5">
                          <Accordion
                            className={"basis-1/4 h-fit bg-gray-100"}
                            expandIcon={[
                              <Icons className="w-4 h-4 m-2" icon="heroicons-outline:plus" />,
                              <Icons className="w-4 h-4 m-2" icon="heroicons-outline:minus" />,
                            ]}
                            footer={
                              <div className="flex items-center justify-between w-full mt-4">
                                <Button shape="circle" icon={<ControlIcon className="w-4 h-4" />} />
                                <Button shape="circle" type="text" size="small" icon={<InfoSettings className="text-[#A4A7AE]" />} />
                              </div>
                            }
                          >
                            <div className="flex flex-col justify-center w-full px-1"></div>
                          </Accordion>

                          <Cards classname={'basis-3/4 p-5'} childClassName={"flex-col justify-between"} variant="secondary">
                            <div className="flex justify-between w-full">
                              <div className="flex gap-2"></div>
                              <Button type="text" icon={<ChevronOutlineRightIcon />}></Button>
                            </div>
                          </Cards>
                        </div>
                      </div>
                    )
                  }

                  {/* Adding Widgets */}
                  {(widgets.length === 0 && !isQuestionLoading && !bookmarkLoading && !isLoadingBookmarks) && <p className="text-center text-sm text-gray-600">No widgets added yet</p>}

                  {(bookmarkLoading || isLoadingBookmarks) ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex flex-col w-full border border-[#E9EAEB] rounded-xl p-3 shadow-sm">
                          <div className="flex flex-col md:flex-row items-start justify-between pb-4">
                            <div className="flex items-start gap-2 flex-1 w-full">
                              <Skeleton.Avatar active size={36} shape="square" />
                              <div className="flex-1 min-w-0 w-full">
                                <Skeleton active paragraph={{ rows: 2 }} />
                              </div>
                            </div>
                            <div className="flex gap-2 items-center justify-end w-full md:w-auto mt-2 md:mt-0">
                              <Skeleton.Button active size="small" />
                              <Skeleton.Avatar active size={32} shape="circle" />
                              <Skeleton.Avatar active size={32} shape="circle" />
                            </div>
                          </div>
                          <div className="mt-2">
                            <Skeleton active paragraph={{ rows: 4 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    widgetList
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <AntdModal
        footer={null}
        open={isOpenCreateDashboard}
        centered
        className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
      // confirmLoading={confirmLoading}
      >
        <div className="p-5">
          <h4 className="text-lg font-semibold">Create a new dashboard</h4>
          <div className="flex flex-col gap-4 mt-5" onClick={(e) => e.stopPropagation()}>
            <InputField
              label="Enter a name for your new dashboard"
              name="dashboard_name"
              control={control}
              placeholder="Example Analysis"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex justify-end space-x-2 mt-1">
              <Button
                type="default"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenCreateDashboard(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateDashboard();
                }}
              >
                Add to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </AntdModal>
      <AntdModal
        footer={null}
        onClose={() => setEditingDashboardName('')}
        open={isOpenEditDashboard}
        centered
        className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
      >
        <div className="p-5">
          <h4 className="text-lg font-semibold">Rename your dashboard</h4>
          <div className="flex flex-col gap-4 mt-5">
            <InputField
              label="Dashboard Name"
              name="editDashboardName"
              value={editingDashboardName} // Shows the previous name
              onChange={(e) => setEditingDashboardName(e.target.value)}
              placeholder={`Enter new dashboard name (current name: ${editingDashboardName})`}
            />
            <div className="flex justify-end space-x-2">
              <Button
                type="default"
                onClick={() => {
                  setOpenEditDashboard(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSubmitEditDashboard}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </AntdModal>
      {/* Delete Dashboard Modal */}
      <AntdModal
        footer={null}
        centered
        className="xl:w-[35%] lg:w-[50%] md:w-[70%] sm:w-[80%]"
        open={isDeleteModalOpen}
        onOk={() => setIsDeleteModalOpen(false)}
        onCancel={() => setIsDeleteModalOpen(false)}
      >
        <div className="relative p-4 flex flex-row items-start gap-5">
          <IconRings numRings={5} ringBaseSize={48} ringSpacing={56} className="-z-10">
            <div className="bg-[#FEE4E2] p-3 w-fit h-fit rounded-full">
              <TrashIcon className="text-[#D92D20] w-5 h-5" />
            </div>
          </IconRings>
          <div className="z-10">
            <h4 className="whitespace-nowrap">Delete Dashboard</h4>
            <p className="text-sm">
              Are you sure you want to delete the dashboard "{dashboardToDelete}"? This action cannot be undone.
            </p>
          </div>
        </div>
        <div className="p-4 flex flex-row justify-end items-center">
          <div className="space-x-4">
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                setIsDeleteModalOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              danger
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDashboard();
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </AntdModal>
      <AntdModal
        closeIcon={null}
        footer={null}
        open={isOpenVisualModal?.state}
        centered
        width={1000}
        onOk={() => setOpenVisualModal({
          state: false,
          type: null
        })}
        // confirmLoading={confirmLoading}
        onCancel={() => setOpenVisualModal({
          state: false,
          type: null
        })}
        styles={{
          body: {
            maxHeight: 'calc(90vh - 100px)',
            overflowY: 'auto'
          }
        }}
      >
        <Button onClick={() => handleVisualModal(null, false, {})} shape="circle" className='bg-gray-100 absolute right-0 z-10 m-1' icon={<CollapseIcon className={"w-3 h-3"} />} />

        {isOpenVisualModal?.type === 'graph' ?
          (() => {
            const chart = isOpenVisualModal?.response?.chart;
            const chartType = chart?.type || 'bar-chart';

            // Determine which data to use based on chart type
            let chartData;

            // Check if data is already an array (old format) or has nested structure (new format)
            const isDirectArray = Array.isArray(chart?.data);

            if (isDirectArray) {
              // Old format: data is directly an array
              chartData = chart.data;
            } else {
              // New format: data is an object with keys like line_graph, pie_chart, etc.
              switch (chartType) {
                case 'pie-chart':
                case 'donut-chart':
                  chartData = chart?.data?.pie_chart || chart?.data?.pie || chart?.data?.table;
                  break;
                case 'line-graph':
                  chartData = chart?.data?.line_graph || chart?.data?.line_chart || chart?.data?.bar_chart || chart?.data?.table;
                  break;
                case 'scatter-plot':
                  chartData = chart?.data?.scatter_plot || chart?.data?.scatter || chart?.data?.table;
                  break;
                case 'geo-map':
                  chartData = chart?.data?.geo_map || chart?.data?.geo || chart?.data?.table;
                  break;
                case 'horizontal-bar':
                case 'multi-horizontal-bar':
                case 'multi-bar':
                case 'bar-chart':
                default:
                  chartData = chart?.data?.bar_chart || chart?.data?.bar || chart?.data?.line_graph || chart?.data?.table;
                  break;
              }
            }

            // Validate that we have valid data array
            if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
              return (
                <div className="w-full min-h-[500px] flex items-center justify-center">
                  <div className="text-gray-400">No chart data available</div>
                </div>
              );
            }

            return (
              <div className="w-full min-h-fit p-4 overflow-auto">
                <ChartErrorBoundary data={chartData}>
                  <CustomChart
                    className='w-full'
                    data={chartData}
                    type={chartType}
                    size='large'
                    dataXkey={isOpenVisualModal?.response?.chart?.dataXKey}
                    dataYKey={isOpenVisualModal?.response?.chart?.dataYKey}
                    xAxisLabel={isOpenVisualModal?.response?.chart?.xAxisLabel}
                    yAxisLabel={isOpenVisualModal?.response?.chart?.yAxisLabel}
                    fill={isOpenVisualModal?.response?.colorScheme || '#1F7A50'}
                    isResponsive={false}
                  />
                </ChartErrorBoundary>
              </div>
            );
          })() :
          (() => {
            const chartData = isOpenVisualModal?.response?.chart?.data;

            // Extract table data correctly - ALWAYS prioritize 'table' key for table modal
            let tableData = [];

            if (Array.isArray(chartData)) {
              // Old format: data is directly an array
              tableData = chartData;
            } else if (chartData && typeof chartData === 'object') {
              // New format: ALWAYS use 'table' key if it exists
              if (chartData.table && Array.isArray(chartData.table) && chartData.table.length > 0) {
                tableData = chartData.table;
              } else {
                // Fallback: find any array in the data object
                const keys = Object.keys(chartData);
                for (const key of keys) {
                  if (Array.isArray(chartData[key]) && chartData[key].length > 0) {
                    tableData = chartData[key];
                    break;
                  }
                }
              }
            }

            return (
              <div className="mt-0 w-full">
                <div className="bg-white rounded-lg shadow-sm overflow-auto max-h-[calc(90vh-150px)]">
                  <DynamicTable
                    key={`table-modal-${modalKeyRef.current}`}
                    data={tableData}
                    showPagination={true}
                    sortable={true}
                  />
                </div>
              </div>
            );
          })()
        }
      </AntdModal>
    </FormProvider>
  );
};

export default Dashboard;